import type { ReactNode } from "react";
import { allDocPages } from "./content";
import DocsLayout from "./layout";

const markdown = `
NewsRPM is CloudQuote's high-throughput news service. It ingests articles from 100+ premium publishers, normalizes ticker tags and metadata, and exposes a small set of JSON endpoints for retrieval.

## Base URL

\`\`\`
https://api.newsrpm.com
\`\`\`

## Authentication

NewsRPM uses the same \`Authorization\` header style as the rest of the platform:

\`\`\`
Authorization: privateKey your_key_goes_here
\`\`\`

Requests originating from a browser must use a **public key** instead — and the origin domain must be whitelisted. Requests from a browser that include a private key are rejected outright.

\`\`\`
Authorization: publicKey jysxsi75p6n
\`\`\`

## Endpoint: \`POST /search/indexedData\`

Search the news index for articles matching one or more criteria, ordered newest-first.

### Example: latest 30 articles mentioning Apple

\`\`\`bash
curl -X POST \\
  -H 'Authorization: privateKey YOUR_KEY' \\
  -H 'Content-Type: application/json' \\
  --data '{
    "key": "PrefixedTicker",
    "value": ["NQ:AAPL", "NY:IBM"],
    "count": 30,
    "order": "dateWithQuality"
  }' \\
  https://api.newsrpm.com/search/indexedData
\`\`\`

### Example: latest 30 press releases mentioning Apple

\`\`\`bash
curl -X POST \\
  -H 'Authorization: privateKey YOUR_KEY' \\
  -H 'Content-Type: application/json' \\
  --data '{
    "key": "PrefixedTicker-Type",
    "value": "NQ:AAPL-Press Release",
    "count": 30,
    "order": "dateWithQuality"
  }' \\
  https://api.newsrpm.com/search/indexedData
\`\`\`

### Request body

| Parameter | Meaning | Type |
| :---- | :---- | :---- |
| \`key\` | The name of the index to search against (see *Index names* below). | String |
| \`value\` | The value to look up in the index. May be a single string or an array. | String or Array |
| \`count\` | Number of articles to return. | Integer |
| \`order\` | Sort order. One of \`date\` or \`dateWithQuality\`. | String |

### Response

\`\`\`json
{
  "rows": [
    { /* Article object */ },
    { /* Article object */ }
  ]
}
\`\`\`

## Endpoint: \`GET /body/{bodyId}/render\`

Render the body of a single article. NewsRPM converts the publisher's native body format to clean HTML chunks.

\`\`\`bash
curl -H 'Authorization: privateKey YOUR_KEY' \\
  https://api.newsrpm.com/body/3yn3RuaDmoGuyC-uBcavppL_5MyAOhov/render
\`\`\`

The response is a \`Body\` object with a \`chunks\` array. Render chunks in order, or rearrange them client-side (e.g., to surface video chunks first).

## Endpoint: \`GET /article/slug/{slug}\`

Retrieve a single article by its slug.

\`\`\`bash
curl -H 'Authorization: privateKey YOUR_KEY' \\
  https://api.newsrpm.com/article/slug/ibdlink-2018-3-15-heres-the-upshot-on-possible-mergers-in-the-optical-industry
\`\`\`

## Index names

| Index | Use it for | Example values |
| :---- | :---- | :---- |
| \`NormalizedTicker\` | Plain ticker symbols. | \`AAPL\`, \`GOOG\`, \`MSFT\` |
| \`NormalizedTicker-Type\` | Ticker + article type. | \`AAPL-Press Release\`, \`GOOG-News\` |
| \`PrefixedTicker\` | Exchange-prefixed tickers. | \`NQ:AAPL\`, \`NY:IBM\` |
| \`PrefixedTicker-Type\` | Prefixed ticker + type. | \`NQ:AAPL-Press Release\` |
| \`FIGI\` (off by default) | Securities by FIGI number. | \`BBG000B9XRY4\` |

## The \`Article\` object

| Key | Meaning | Type |
| :---- | :---- | :---- |
| \`provider\` | Syndication partner that delivered the article. **Required.** | String |
| \`publisher\` | The actual publisher, when known. | String |
| \`headline\` | The article headline. **Required.** | String |
| \`link\` | Canonical URL of the article. | String |
| \`date\` | Publication timestamp. **Required.** | ISO 8601 |
| \`expires\` | Expiration timestamp, if any. | ISO 8601 |
| \`slug\` | Unique identifier for the article. **Required.** | String |
| \`summary\` | Short summary. | Unsanitized HTML |
| \`firstParagraph\` | First paragraph of the body. | Unsanitized HTML |
| \`bodyId\` | Pass to \`/body/{bodyId}/render\` to fetch the body. | String |
| \`language\` | Language code. | String |
| \`visibility\` | One of \`draft\`, \`embargo\`, \`published\`, \`retracted\`, \`archive\`. **Required.** | String |
| \`quality\` | Quality score; useful for ranking. **Required.** | Signed integer |
| \`metaData\` | Provider-specific metadata (categories, tickers, etc.). | JSON |

> The \`id\` field is **deprecated**. IDs are instance-specific in sharded deployments — use \`slug\` for cross-instance references.

## The \`Body\` object

| Key | Meaning | Type |
| :---- | :---- | :---- |
| \`v\` | Body specification version. \`1.0\` today. | Number |
| \`chunks\` | Ordered array of \`BodyChunk\` objects. | Array |

### \`BodyChunk\`

| Key | Meaning | Type |
| :---- | :---- | :---- |
| \`name\` | Provider-defined chunk name. | String |
| \`format\` | MIME type — usually \`text/html\`. | String |
| \`content\` | Chunk content. For \`text/html\`, the HTML string. | String |

## Where to go next

- To **publish** news into a NewsRPM instance, see [Publishing News via RSS](/docs/rss-feeds).
- For **semantic** news search (e.g., "EV battery startups raising Series B"), use the [Search API](/docs/search-api).
- To let an LLM orchestrate news + market data calls, use the [Chat API](/docs/chat-api).
`;

export default function NewsRpmApiPage(): ReactNode {
  const page = allDocPages.find(p => p.slug === "newsrpm-api")!;
  return <DocsLayout page={page} markdown={markdown}/>;
}
